import React from "react";

/**
 * Per-product animated hero scene.
 * Dispatched via the `viz` prop (matches product.heroViz).
 * Each scene is unique and illustrative of the product's purpose.
 */
export function ProductHero({ viz }) {
  const map = {
    swimlane: <Swimlane />,
    radar: <RadarScene />,
    vendorgrid: <VendorGrid />,
    agents: <AgentsNetwork />,
    vault: <DocVault />,
    plantlayout: <PlantLayout />,
    controltower: <ControlTower />,
    kraljic: <KraljicScene />,
  };
  return (
    <div className="product-hero-viz" data-testid="product-hero-viz">
      <style>{`
        @keyframes drawPath { from { stroke-dashoffset: var(--dash); } to { stroke-dashoffset: 0; } }
        @keyframes flip { 0%,45% { transform: rotateY(0); } 50%,95% { transform: rotateY(180deg); } 100% { transform: rotateY(360deg); } }
        @keyframes pulseRing { 0% { transform: scale(0.7); opacity: 0.9; } 100% { transform: scale(2.4); opacity: 0; } }
        @keyframes thoughtBubble { 0%,100% { opacity: 0.3; transform: translateY(0); } 50% { opacity: 1; transform: translateY(-4px); } }
        @keyframes morphShape { 0%,100% { d: path('M 0 0'); } }
      `}</style>
      {map[viz] || <Swimlane />}
    </div>
  );
}

/* ── A1 · Luminex · Swimlane self-assembly ─────────────── */
function Swimlane() {
  return (
    <svg viewBox="0 0 720 480" className="product-hero-svg" aria-hidden="true">
      <defs>
        <linearGradient id="laneG" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#1E4E8C" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#2E70AD" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* swimlanes */}
      {[0, 1, 2, 3].map((i) => (
        <g key={i}>
          <rect x="60" y={80 + i * 80} width="620" height="64" rx="6" fill="url(#laneG)" stroke="#D9DDE3" />
          <text x="72" y={115 + i * 80} fontFamily="JetBrains Mono" fontSize="9" fill="#4B5563" letterSpacing="2">
            {["ENGINEERING", "PROCUREMENT", "CONSTRUCTION", "COMMISSIONING"][i]}
          </text>
        </g>
      ))}
      {/* process boxes with staggered entrance */}
      {[
        { lane: 0, x: 170, color: "#1E4E8C", label: "FEED" },
        { lane: 0, x: 300, color: "#1E4E8C", label: "DED" },
        { lane: 1, x: 250, color: "#2E70AD", label: "RFx" },
        { lane: 1, x: 400, color: "#2E70AD", label: "PO" },
        { lane: 1, x: 540, color: "#F26B1F", label: "RISK" },
        { lane: 2, x: 420, color: "#2E70AD", label: "ERECT" },
        { lane: 2, x: 550, color: "#2E70AD", label: "WELD" },
        { lane: 3, x: 580, color: "#1E4E8C", label: "HAND" },
      ].map((b, i) => (
        <g
          key={i}
          style={{ animation: `build 0.7s ${0.2 + i * 0.12}s cubic-bezier(.2,.8,.2,1) backwards`, transformOrigin: `${b.x + 40}px ${90 + b.lane * 80}px` }}
        >
          <rect x={b.x} y={90 + b.lane * 80} width="80" height="44" rx="6" fill={b.color === "#F26B1F" ? "#F26B1F" : "#FFFFFF"} stroke={b.color} strokeWidth={b.color === "#F26B1F" ? 2 : 1.4} />
          {b.color === "#F26B1F" && <circle cx={b.x + 40} cy={112 + b.lane * 80} r="20" fill="#F26B1F" opacity="0.2" style={{ animation: "pulseRing 2s ease-out infinite" }} />}
          <text x={b.x + 40} y={117 + b.lane * 80} textAnchor="middle" fontFamily="JetBrains Mono" fontSize="11" fill={b.color === "#F26B1F" ? "#FFFFFF" : b.color} fontWeight="700">{b.label}</text>
        </g>
      ))}
      {/* flow arrows */}
      <g stroke="#1E4E8C" fill="none" strokeWidth="1.2" strokeLinecap="round">
        {[
          "M 250 112 L 300 112",
          "M 380 112 Q 410 112 410 170",
          "M 330 170 L 400 170",
          "M 480 170 L 540 170",
          "M 480 170 Q 480 230 500 250",
          "M 500 250 L 550 250",
          "M 630 250 Q 650 290 620 320",
        ].map((d, i) => (
          <path
            key={i}
            d={d}
            strokeDasharray="180"
            style={{ "--dash": "180", animation: `drawPath 1.2s ${0.8 + i * 0.15}s cubic-bezier(.2,.8,.2,1) backwards` }}
          />
        ))}
      </g>
      <text x="560" y="72" fontFamily="JetBrains Mono" fontSize="9" fill="#F26B1F" letterSpacing="2">⚠ BOTTLENECK DETECTED</text>
    </svg>
  );
}

/* ── A2 · Assayo · Radar morph ─────────────── */
function RadarScene() {
  const axes = 12;
  const cx = 360, cy = 240, r = 140;
  const curr = [0.55, 0.4, 0.6, 0.35, 0.5, 0.7, 0.45, 0.5, 0.4, 0.6, 0.55, 0.3];
  const target = [0.85, 0.8, 0.9, 0.75, 0.85, 0.9, 0.8, 0.85, 0.75, 0.9, 0.85, 0.7];
  const pts = (vals) => vals.map((v, i) => {
    const a = (i / axes) * Math.PI * 2 - Math.PI / 2;
    return `${cx + Math.cos(a) * r * v},${cy + Math.sin(a) * r * v}`;
  }).join(" ");
  return (
    <svg viewBox="0 0 720 480" className="product-hero-svg" aria-hidden="true">
      {/* concentric rings */}
      {[0.25, 0.5, 0.75, 1].map((f, i) => (
        <circle key={i} cx={cx} cy={cy} r={r * f} fill="none" stroke="#D9DDE3" strokeWidth="1" strokeDasharray={i === 3 ? "0" : "3 4"} />
      ))}
      {/* axes */}
      {Array.from({ length: axes }).map((_, i) => {
        const a = (i / axes) * Math.PI * 2 - Math.PI / 2;
        const x = cx + Math.cos(a) * r;
        const y = cy + Math.sin(a) * r;
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="#D9DDE3" strokeWidth="0.8" />;
      })}
      {/* current */}
      <polygon points={pts(curr)} fill="#1E4E8C" fillOpacity="0.2" stroke="#1E4E8C" strokeWidth="1.4" />
      {curr.map((v, i) => {
        const a = (i / axes) * Math.PI * 2 - Math.PI / 2;
        return <circle key={i} cx={cx + Math.cos(a) * r * v} cy={cy + Math.sin(a) * r * v} r="3" fill="#1E4E8C" />;
      })}
      {/* target (stable, non-pulsing) */}
      <polygon points={pts(target)} fill="#F26B1F" fillOpacity="0.15" stroke="#F26B1F" strokeWidth="1.6" strokeDasharray="5 4" />
      {target.map((v, i) => {
        const a = (i / axes) * Math.PI * 2 - Math.PI / 2;
        return <circle key={i} cx={cx + Math.cos(a) * r * v} cy={cy + Math.sin(a) * r * v} r="3" fill="#F26B1F" />;
      })}
      {/* labels */}
      {["ENG", "PROC", "CONST", "COMM", "HSE", "QC", "CTRL", "CONTR", "DIGITAL", "HR", "FIN", "SUS"].map((lbl, i) => {
        const a = (i / axes) * Math.PI * 2 - Math.PI / 2;
        const x = cx + Math.cos(a) * (r + 20);
        const y = cy + Math.sin(a) * (r + 20);
        return <text key={i} x={x} y={y} fontFamily="JetBrains Mono" fontSize="9" textAnchor="middle" fill="#4B5563" letterSpacing="1">{lbl}</text>;
      })}
      <g transform="translate(60 400)">
        <rect width="10" height="10" fill="#1E4E8C" /><text x="18" y="9" fontFamily="JetBrains Mono" fontSize="10" fill="#4B5563">Current state</text>
        <rect x="160" width="10" height="10" fill="#F26B1F" /><text x="178" y="9" fontFamily="JetBrains Mono" fontSize="10" fill="#4B5563">Target state</text>
      </g>
    </svg>
  );
}

/* ── A3 · Evalore · Vendor grid flip ─────────────── */
function VendorGrid() {
  const vendors = ["legacy schedulers", "legacy schedulers", "enterprise ERP", "enterprise ERP", "3D plant-design suites", "Autodesk-class CAD/BIM", "plant-engineering platforms", "engineering 3D suites", "IFS", "Cora", "InEight", "Synchro"];
  return (
    <svg viewBox="0 0 720 480" className="product-hero-svg" aria-hidden="true">
      <text x="60" y="50" fontFamily="JetBrains Mono" fontSize="10" fill="#4B5563" letterSpacing="2">SCORED · 200+ CRITERIA</text>
      {vendors.map((v, i) => {
        const col = i % 4, row = Math.floor(i / 4);
        const x = 60 + col * 160;
        const y = 80 + row * 120;
        const score = [88, 75, 82, 68, 91, 79, 72, 85, 60, 73, 66, 80][i];
        const winner = score >= 85;
        return (
          <g key={v} style={{ animation: `flip 6s ${i * 0.4}s ease-in-out infinite`, transformOrigin: `${x + 70}px ${y + 45}px` }}>
            <rect x={x} y={y} width="140" height="90" rx="8" fill={winner ? "#1E4E8C" : "#FFFFFF"} stroke={winner ? "#1E4E8C" : "#D9DDE3"} strokeWidth="1.4" />
            <text x={x + 14} y={y + 26} fontFamily="JetBrains Mono" fontSize="10" fill={winner ? "#C2D5EE" : "#9AA2B1"} letterSpacing="1">VENDOR {String(i + 1).padStart(2, "0")}</text>
            <text x={x + 14} y={y + 52} fontFamily="Bricolage Grotesque" fontSize="22" fontWeight="600" fill={winner ? "#FFFFFF" : "#0F131C"}>{v}</text>
            <text x={x + 14} y={y + 78} fontFamily="JetBrains Mono" fontSize="11" fill={winner ? "#F26B1F" : "#1E4E8C"} fontWeight="700">SCORE · {score}</text>
          </g>
        );
      })}
    </svg>
  );
}

/* ── A4 · Cognara · Agent neural network ─────────────── */
function AgentsNetwork() {
  const nodes = [
    { id: "core", x: 360, y: 240, label: "ORCHESTRATOR", big: true },
    { id: "a", x: 160, y: 120, label: "CONTRACTS" },
    { id: "b", x: 560, y: 120, label: "SCHEDULE" },
    { id: "c", x: 120, y: 280, label: "RISK" },
    { id: "d", x: 600, y: 280, label: "HSE" },
    { id: "e", x: 240, y: 400, label: "COMMERCIAL" },
    { id: "f", x: 480, y: 400, label: "CODES" },
  ];
  const core = nodes[0];
  return (
    <svg viewBox="0 0 720 480" className="product-hero-svg" aria-hidden="true">
      {/* lines from core */}
      {nodes.slice(1).map((n, i) => (
        <line key={i} x1={core.x} y1={core.y} x2={n.x} y2={n.y} stroke="#2E70AD" strokeOpacity="0.35" strokeWidth="1" strokeDasharray="4 4" style={{ animation: `flowDash ${4 + i * 0.3}s linear infinite` }} />
      ))}
      {/* core */}
      <circle cx={core.x} cy={core.y} r="50" fill="#1E4E8C" fillOpacity="0.12" style={{ animation: "pulseRing 3s ease-out infinite" }} />
      <circle cx={core.x} cy={core.y} r="28" fill="#1E4E8C" />
      <text x={core.x} y={core.y + 4} textAnchor="middle" fontFamily="JetBrains Mono" fontSize="9" fill="#FFFFFF" letterSpacing="1">CORE</text>
      {/* agents */}
      {nodes.slice(1).map((n, i) => (
        <g key={n.id} style={{ animation: `thoughtBubble 3s ease-in-out ${i * 0.3}s infinite` }}>
          <circle cx={n.x} cy={n.y} r="26" fill="#FFFFFF" stroke="#1E4E8C" strokeWidth="1.4" />
          <circle cx={n.x} cy={n.y} r="6" fill="#1E4E8C" />
          <text x={n.x} y={n.y + 46} textAnchor="middle" fontFamily="JetBrains Mono" fontSize="9" fill="#4B5563" letterSpacing="1">{n.label}</text>
          {/* thought bubble */}
          <g transform={`translate(${n.x - 40} ${n.y - 60})`} style={{ opacity: 0.75 }}>
            <rect x="0" y="0" width="80" height="18" rx="9" fill="#F26B1F" fillOpacity="0.12" stroke="#F26B1F" strokeOpacity="0.4" />
            <text x="40" y="12" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="8" fill="#F26B1F">reasoning…</text>
          </g>
        </g>
      ))}
    </svg>
  );
}

/* ── B1 · Docuweave · Document vault ─────────────── */
function DocVault() {
  return (
    <svg viewBox="0 0 720 480" className="product-hero-svg" aria-hidden="true">
      {/* rotating drawings stack */}
      {[0, 1, 2, 3, 4].map((i) => (
        <g key={i} transform={`translate(${200 + i * 8} ${80 + i * 10}) rotate(${-6 + i * 2} 110 140)`}>
          <rect x="0" y="0" width="220" height="280" rx="4" fill="#FFFFFF" stroke="#D9DDE3" strokeWidth="1" style={{ filter: `drop-shadow(0 ${2 + i}px ${4 + i * 2}px rgba(15,19,28,0.1))` }} />
          {/* drawing content */}
          <line x1="20" y1="20" x2="200" y2="20" stroke="#D9DDE3" />
          <rect x="20" y="30" width="180" height="100" fill="none" stroke="#4B5563" strokeWidth="0.6" />
          <line x1="20" y1="60" x2="200" y2="60" stroke="#4B5563" strokeWidth="0.3" />
          <line x1="60" y1="30" x2="60" y2="130" stroke="#4B5563" strokeWidth="0.3" />
          <line x1="120" y1="30" x2="120" y2="130" stroke="#4B5563" strokeWidth="0.3" />
          {/* revision cloud */}
          {i === 2 && <path d="M 100 160 Q 90 150 100 140 Q 90 130 105 125 Q 120 120 135 135 Q 150 145 140 155 Q 130 170 115 165 Q 105 170 100 160 Z" fill="#F26B1F" fillOpacity="0.15" stroke="#F26B1F" strokeDasharray="3 2" />}
          {/* tag */}
          {i === 4 && <rect x="15" y="248" width="50" height="16" fill="#1E4E8C" />}
          {i === 4 && <text x="40" y="260" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="8" fill="#FFFFFF">REV 05</text>}
        </g>
      ))}
      {/* Tag network overlay */}
      {[[80, 180], [560, 140], [100, 380], [580, 380]].map(([x, y], i) => (
        <g key={i}>
          <line x1="320" y1="200" x2={x} y2={y} stroke="#1E4E8C" strokeOpacity="0.4" strokeWidth="1" strokeDasharray="3 3" />
          <circle cx={x} cy={y} r="12" fill="#1E4E8C" fillOpacity="0.14" />
          <circle cx={x} cy={y} r="5" fill="#1E4E8C" style={{ animation: "pulseDot 2s ease-in-out infinite", animationDelay: `${i * 0.4}s` }} />
          <text x={x} y={y + 24} textAnchor="middle" fontFamily="JetBrains Mono" fontSize="8" fill="#4B5563">{["P-101", "E-204", "V-301", "T-502"][i]}</text>
        </g>
      ))}
    </svg>
  );
}

/* ── B2 · Formigen · Plant layout morph ─────────────── */
function PlantLayout() {
  return (
    <svg viewBox="0 0 720 480" className="product-hero-svg" aria-hidden="true">
      <text x="60" y="50" fontFamily="JetBrains Mono" fontSize="10" fill="#4B5563" letterSpacing="2">LAYOUT · ITERATION 4 OF 5</text>
      {/* grid */}
      {Array.from({ length: 12 }).map((_, i) => (
        <line key={`v${i}`} x1={60 + i * 48} y1="80" x2={60 + i * 48} y2="420" stroke="#EEF0F4" strokeWidth="1" />
      ))}
      {Array.from({ length: 8 }).map((_, i) => (
        <line key={`h${i}`} x1="60" y1={80 + i * 44} x2="636" y2={80 + i * 44} stroke="#EEF0F4" strokeWidth="1" />
      ))}
      {/* equipment blocks */}
      {[
        { x: 100, y: 120, w: 80, h: 60, label: "V-101", color: "#1E4E8C" },
        { x: 250, y: 120, w: 100, h: 80, label: "T-201", color: "#2E70AD" },
        { x: 430, y: 140, w: 60, h: 60, label: "P-301", color: "#1E4E8C" },
        { x: 540, y: 160, w: 70, h: 40, label: "E-401", color: "#2E70AD" },
        { x: 150, y: 260, w: 60, h: 60, label: "R-501", color: "#1E4E8C" },
        { x: 300, y: 280, w: 90, h: 60, label: "HX-601", color: "#2E70AD" },
        { x: 470, y: 280, w: 80, h: 60, label: "C-701", color: "#1E4E8C" },
      ].map((b, i) => (
        <g key={i} style={{ animation: `thoughtBubble 4s ease-in-out ${i * 0.2}s infinite` }}>
          <rect x={b.x} y={b.y} width={b.w} height={b.h} fill={b.color} fillOpacity="0.15" stroke={b.color} strokeWidth="1.4" />
          <text x={b.x + b.w / 2} y={b.y + b.h / 2 + 4} textAnchor="middle" fontFamily="JetBrains Mono" fontSize="10" fill={b.color} fontWeight="700">{b.label}</text>
        </g>
      ))}
      {/* pipe routing (animated flow) */}
      {[
        "M 140 180 L 140 200 L 250 200",
        "M 350 160 L 430 160",
        "M 490 170 L 540 180",
        "M 180 290 L 300 290",
        "M 390 310 L 470 310",
        "M 350 200 L 350 260 L 330 280",
      ].map((d, i) => (
        <path key={i} d={d} fill="none" stroke="#F26B1F" strokeWidth="2" strokeDasharray="6 6" style={{ animation: `flowDash ${2 + i * 0.3}s linear infinite` }} />
      ))}
    </svg>
  );
}

/* ── B3 · Panorix · Control Tower ─────────────── */
function ControlTower() {
  return (
    <svg viewBox="0 0 720 480" className="product-hero-svg" aria-hidden="true">
      <rect x="40" y="40" width="640" height="400" rx="10" fill="#0A0E1A" />
      <text x="60" y="68" fontFamily="JetBrains Mono" fontSize="10" fill="#F26B1F" letterSpacing="2">◉ LIVE · CAPITAL PROJECTS CONTROL TOWER</text>
      {/* KPI tiles */}
      {[
        { x: 60, y: 88, w: 130, label: "ACTIVE PROJECTS", val: "14", color: "#2E70AD" },
        { x: 200, y: 88, w: 130, label: "CPI (portfolio)", val: "1.04 ▲", color: "#F26B1F" },
        { x: 340, y: 88, w: 130, label: "SPI (portfolio)", val: "0.98", color: "#2E70AD" },
        { x: 480, y: 88, w: 180, label: "COMMITTED CAPEX", val: "₹14,280 Cr", color: "#C2D5EE" },
      ].map((t, i) => (
        <g key={i}>
          <rect x={t.x} y={t.y} width={t.w} height="58" rx="4" fill="#121622" stroke="rgba(255,255,255,0.1)" />
          <text x={t.x + 10} y={t.y + 18} fontFamily="JetBrains Mono" fontSize="8" fill="#9AA2B1" letterSpacing="1.5">{t.label}</text>
          <text x={t.x + 10} y={t.y + 44} fontFamily="Bricolage Grotesque" fontSize="20" fill={t.color} fontWeight="600">{t.val}</text>
        </g>
      ))}
      {/* Chart */}
      <rect x="60" y="160" width="400" height="180" rx="4" fill="#121622" stroke="rgba(255,255,255,0.1)" />
      <text x="76" y="180" fontFamily="JetBrains Mono" fontSize="8" fill="#9AA2B1" letterSpacing="1.5">EVM · 12-WEEK TREND</text>
      {/* axis */}
      <line x1="80" y1="310" x2="440" y2="310" stroke="rgba(255,255,255,0.2)" />
      <line x1="80" y1="200" x2="80" y2="310" stroke="rgba(255,255,255,0.2)" />
      {/* bars */}
      {[60, 72, 66, 80, 74, 88, 82, 90, 85, 92, 96, 102].map((h, i) => (
        <rect key={i} x={90 + i * 28} y={310 - h} width="20" height={h} fill="#2E70AD" opacity={0.5 + i * 0.04} style={{ animation: `build 0.8s ${i * 0.08}s cubic-bezier(.2,.8,.2,1) backwards` }} />
      ))}
      {/* trend line */}
      <path d="M 100 250 L 128 238 L 156 244 L 184 230 L 212 234 L 240 222 L 268 226 L 296 218 L 324 222 L 352 214 L 380 208 L 408 200" fill="none" stroke="#F26B1F" strokeWidth="2" />
      {/* 3D plant tile */}
      <rect x="480" y="160" width="180" height="180" rx="4" fill="#121622" stroke="rgba(255,255,255,0.1)" />
      <text x="494" y="180" fontFamily="JetBrains Mono" fontSize="8" fill="#9AA2B1" letterSpacing="1.5">4D TIMELINER</text>
      <g transform="translate(570 250)">
        <polygon points="0,-20 40,0 0,20 -40,0" fill="rgba(46,112,173,0.25)" stroke="#2E70AD" />
        <polygon points="-40,0 0,20 0,60 -40,40" fill="rgba(30,78,140,0.3)" stroke="#1E4E8C" />
        <polygon points="0,20 40,0 40,40 0,60" fill="rgba(15,19,28,0.6)" stroke="#3A3F4A" />
        <circle cx="0" cy="-30" r="4" fill="#F26B1F" style={{ animation: "pulseDot 2s ease-in-out infinite" }} />
      </g>
      {/* Alerts */}
      <rect x="60" y="354" width="600" height="62" rx="4" fill="#121622" stroke="rgba(255,255,255,0.1)" />
      <text x="76" y="374" fontFamily="JetBrains Mono" fontSize="8" fill="#9AA2B1" letterSpacing="1.5">ALERTS · 3 NEW</text>
      <circle cx="88" cy="395" r="4" fill="#F26B1F" style={{ animation: "pulseDot 2s ease-in-out infinite" }} />
      <text x="100" y="399" fontFamily="JetBrains Mono" fontSize="10" fill="#F3F4F6">TOWER-A — SPI dipped to 0.92 · recovery plan required</text>
    </svg>
  );
}

/* ── B4 · ProcureX · Kraljic matrix + sourcing cockpit ─────────────── */
function KraljicScene() {
  return (
    <svg viewBox="0 0 720 480" className="product-hero-svg" aria-hidden="true">
      {/* Matrix */}
      <g transform="translate(60 80)">
        <text x="0" y="0" fontFamily="JetBrains Mono" fontSize="10" fill="#4B5563" letterSpacing="2">KRALJIC · SUPPLIER RISK HEAT</text>
        <rect x="0" y="20" width="300" height="300" fill="none" stroke="#D9DDE3" />
        <line x1="150" y1="20" x2="150" y2="320" stroke="#D9DDE3" />
        <line x1="0" y1="170" x2="300" y2="170" stroke="#D9DDE3" />
        {/* labels */}
        <text x="75" y="38" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="9" fill="#4B5563">LEVERAGE</text>
        <text x="225" y="38" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="9" fill="#4B5563">STRATEGIC</text>
        <text x="75" y="188" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="9" fill="#4B5563">NON-CRIT</text>
        <text x="225" y="188" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="9" fill="#4B5563">BOTTLENECK</text>
        {/* heat dots */}
        {[
          { x: 70, y: 140, r: 12, c: "#1E4E8C" },
          { x: 110, y: 90, r: 18, c: "#2E70AD" },
          { x: 200, y: 70, r: 22, c: "#F26B1F" },
          { x: 260, y: 130, r: 16, c: "#F26B1F" },
          { x: 60, y: 250, r: 10, c: "#9AA2B1" },
          { x: 130, y: 280, r: 14, c: "#2E70AD" },
          { x: 220, y: 240, r: 20, c: "#F26B1F" },
          { x: 270, y: 280, r: 12, c: "#1E4E8C" },
        ].map((d, i) => (
          <g key={i}>
            <circle cx={d.x} cy={d.y + 20} r={d.r + 8} fill={d.c} fillOpacity="0.12" style={{ animation: `pulseDot ${2 + i * 0.2}s ease-in-out infinite` }} />
            <circle cx={d.x} cy={d.y + 20} r={d.r} fill={d.c} fillOpacity="0.7" />
          </g>
        ))}
      </g>
      {/* Sourcing cockpit */}
      <g transform="translate(400 80)">
        <rect x="0" y="0" width="260" height="320" rx="8" fill="#0A0E1A" />
        <text x="16" y="24" fontFamily="JetBrains Mono" fontSize="9" fill="#F26B1F" letterSpacing="2">◉ LIVE · REVERSE eAUCTION</text>
        <text x="16" y="44" fontFamily="JetBrains Mono" fontSize="8" fill="#9AA2B1" letterSpacing="1">PO-24-0142 · PIPING MATERIAL</text>
        {/* bid rows */}
        {[
          { bidder: "VENDOR-A", bid: "₹48.2 Cr", rank: "1", highlight: true },
          { bidder: "VENDOR-B", bid: "₹49.6 Cr", rank: "2" },
          { bidder: "VENDOR-C", bid: "₹51.1 Cr", rank: "3" },
          { bidder: "VENDOR-D", bid: "₹54.8 Cr", rank: "4" },
          { bidder: "VENDOR-E", bid: "₹57.2 Cr", rank: "5" },
        ].map((r, i) => (
          <g key={i} transform={`translate(16 ${70 + i * 40})`}>
            <rect width="228" height="30" rx="4" fill={r.highlight ? "#1E4E8C" : "#121622"} stroke="rgba(255,255,255,0.1)" />
            <text x="14" y="20" fontFamily="JetBrains Mono" fontSize="10" fill={r.highlight ? "#F3F4F6" : "#C2D5EE"}>{r.rank}. {r.bidder}</text>
            <text x="150" y="20" fontFamily="JetBrains Mono" fontSize="11" fill={r.highlight ? "#F26B1F" : "#9AA2B1"} fontWeight="700">{r.bid}</text>
          </g>
        ))}
        <text x="16" y="296" fontFamily="JetBrains Mono" fontSize="8" fill="#9AA2B1" letterSpacing="1">TIME LEFT · 00:04:32</text>
      </g>
    </svg>
  );
}
